<template>
  <ValidationProvider
    v-slot="{ errors }"
    class="veeValidate"
    tag="div"
    :name="label ? label : name ? name : 'field'"
    mode="aggressive"
    :rules="rules"
  >
    <div class="rs-multiple-inputs">
      <label v-if="label">{{ label }}</label>
      <BFormTags
        :id="`multiple-inputs-${id}`"
        v-model="input"
        :separator="separator"
        :placeholder="placeholder || $ts('multipleInputs.placeholderDefault')"
        :no-add-on-enter="noAddOnEnter"
        :disabled="disabled"
        :add-button-text="$ts('actions.addItem')"
        :remove-on-delete="removeOnDelete"
        :size="size"
        :state="errors[0] ? false : valid ? true : null"
        :duplicate-tag-text="$ts('multipleInputs.duplicateTagText')"
      />
    </div>
    <div
      v-if="errors.length"
      class="text--error"
    >
      {{ errors[0] }}
    </div>
  </ValidationProvider>
</template>

<script lang="ts">
import { ValidationProvider, extend } from 'vee-validate';
import { PropType, defineComponent } from 'vue';
/* eslint-disable camelcase */
import {
  alpha_num,
  digits,
  email,
  max,
  max_value,
  min,
  min_value,
  numeric,
  regex,
  required,
} from 'vee-validate/dist/rules';
import { methods } from '@/mixins/validation-mixin';
import i18n from '@/i18n';

extend('email', { ...email, message: () => `${i18n.t('validation.emailInvalid')}` });
extend('required', { ...required, message: () => `${i18n.t('validation.fieldRequired')}` });
extend('numeric', numeric);
extend('max', max);
extend('min', min);
extend('max_value', max_value);
extend('min_value', min_value);
extend('digits', digits);
extend('regex', regex);
extend('url', { validate: methods.isURL, message: () => `${i18n.t('validation.urlInvalid')}` });
extend('numSpace', {
  validate: (value) => {
    const trimValue = value.replace(/\s/g, '');
    if (trimValue === '') return true;
    if (
      /^[0-9]*$/im.test(
        trimValue,
      ) === false
    ) {
      return false;
    }
    return true;
  },
  message: () => `${i18n.t('validation.numericOnly')}`,
});
extend('phone', {
  validate: (value) => {
    const trimValue = value.replace(/\s/g, '');
    if (trimValue === '') return true;
    if (
      /^[+]?[0-9]?[-\s.]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im.test(
        trimValue,
      ) === false
    ) {
      return false;
    }
    return true;
  },
  message: () => `${i18n.t('validation.phoneInvalid')}`,
});
extend('alpha_num', {
  ...alpha_num,
  message: () => `${i18n.t('validation.alphaNumOnly')}`,
});
extend('zipRegex', {
  validate: (zip: string) => {
    const zipRegex = new RegExp(
      /^\s*[0-9]{5}\s*$|^\s*[A-Z][0-9][A-Z] ?[0-9][A-Z][0-9]\s*$/,
      'i',
    );
    return zipRegex.test(String(zip).toLowerCase());
  },
  params: ['zipRegex'],
  message: () => `${i18n.t('validation.postalInvalid')}`,
});
extend('urlRegex', {
  validate: methods.isURL,
  params: ['urlRegex'],
  message: () => `${i18n.t('validation.urlRegexInvalid')}`,
});
extend('multiEmail', {
  validate: (value) => {
    const commaSplit = new RegExp('\\s?[, ]\\s?');
    let isValid = true;
    // eslint-disable-next-line no-param-reassign
    value = value.toString().replace(/\n/g, ',');
    let multiEmail = value.split(commaSplit);
    multiEmail = multiEmail.filter((e: any) => e);
    multiEmail = multiEmail.map((e: any) => e.trim());
    multiEmail.forEach((element: any, index: number) => {
      if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(element) === false) {
        isValid = false;
      }
    });

    return isValid;
  },
  message: () => `${i18n.t('validation.emailsNotValid')}`,
});

export default defineComponent({
  name: 'RsMultipleInputs',
  components: {
    ValidationProvider,
  },
  props: {
    /**
     * Validation Rules
     */
    rules: {
      type: Object,
      default: () => {},
    },
    /**
     * Set input tag id.
     */
    id: {
      type: [String, Number],
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    /**
     * Value
     */
    value: {
      type: Array as PropType<string[]|number[]>,
      default: () => [],
    },
    /**
     * Separator characters
     */
    separator: {
      type: String,
      default: '',
    },
    /**
     * Field Name
     */
    name: {
      type: String,
      default: 'input',
    },
    /**
     * Input Placeholder
     */
    placeholder: {
      type: String,
      default: null,
    },
    /**
     * Disable enter to add tag
     */
    noAddOnEnter: {
      type: Boolean,
      default: false,
    },
    /**
     * Enable backspace to delete tag
     */
    removeOnDelete: {
      type: Boolean,
      default: true,
    },
    /**
     * Disable to type
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Input tag size: sm | md | lg
     */
    size: {
      type: String,
      default: 'md',
    },
  },

  data() {
    return {
      valid: null,
      input: this.value as Array<string|number>,
    };
  },

  watch: {
    value() {
      this.input = this.value;
    },
    input() {
      this.$emit('input-change', this.input);
    },
  },
});
</script>

<style lang="scss" scoped>
  @import '@/styles/_variables.scss';
  .rs-multiple-inputs {
    ::v-deep .badge-secondary {
      background: $secondary-color;
      color: $white;
    }
  }
</style>
